// Mirrors chat-frontend's client/events/index.ts. The host can't statically
// import the federated `OpalChat/events` module (all federation imports here are
// lazy), so the cross-frontend event names are duplicated here as the single
// host-side source of truth. Keep keys/values in sync with the remote registry.
export const EVENT_NAME = {
  OPAL_CHATBOX_DOCK_STATE_CHANGED: 'opal::chatbox::dockStateChanged',
  OPAL_CHATBOX_CLOSED: 'opal::chatbox::closed',
  OPAL_THREAD_DELETED: 'opal::thread::deleted',
  OPAL_THREAD_CREATED: 'opal::thread::created',
  OPAL_THREAD_CHANGED: 'opal::thread::changed',
  OPAL_THREAD_TITLE_CHANGED: 'opal::thread::titleChanged',
  OPAL_THREAD_LIST_SHOWN: 'opal::thread::listShown',
  OPAL_CHAT_SET_THREAD: 'opal::chat::setThread',
  OPAL_PROVIDER_SWITCHED: 'opal::provider::switched',
  OPAL_PROMPT_SENT: 'opal::prompt::sent',
  OPAL_PROMPT_FILE_UPLOADED: 'opal::prompt::fileUploaded',
  OPAL_PROMPT_AGENT_MENTIONED: 'opal::prompt::agentMentioned',
  OPAL_PROMPT_LINKED: 'opal::prompt::linked',
  OPAL_RESPONSE_INTERRUPTED: 'opal::response::interrepted',
  OPAL_RESPONSE_COMPLETED: 'opal::response::completed',
  OPAL_RESPONSE_COPIED: 'opal::response::copied',
  OPAL_RESPONSE_DISLIKED: 'opal::response::disliked',
  OPAL_RESPONSE_LIKED: 'opal::response::liked',
  OPAL_RESPONSE_REGENERATED: 'opal::response::regenerated',
  OPAL_RESPONSE_DOWNLOADED: 'opal::response::downloaded',
  OPAL_RESPONSE_REFINED: 'opal::response::refined',
  OPAL_SUGGESTION_ACCEPTED: 'opal::suggestion::accepted',
  OPAL_SUGGESTION_ADDED: 'opal::suggestion::added',
  OPAL_SUGGESTION_SKIPPED: 'opal::suggestion::skipped',
  OPAL_INTERACTION_ISLAND_CLICKED: 'opal::interactionIsland::clicked',
  OPAL_SIDEBAR_VISIBILITY_CHANGED: 'opal::sidebar::visibilityChanged',
  OPAL_UNREAD_THREADS_CHANGED: 'opal::threads::unreadChanged',
  OPAL_IM_MESSAGE_RECEIVED: 'opal::im::messageReceived',
  OPAL_IM_CONVERSATION_CHANGED: 'opal::im::conversationChanged',
  OPAL_IM_MEMBERS_CHANGED: 'opal::im::membersChanged',
  OPAL_IM_MODE_CHANGED: 'opal::im::modeChanged',
  OPAL_IM_UNREAD_COUNT_CHANGED: 'opal::im::unreadCountChanged',
  OPAL_IM_NOTIFICATION_NEW: 'opal::im::notificationNew',
  OPAL_IM_STATE_CHANGED: 'opal::im::stateChanged',
  OPAL_IM_SHARE_OPEN: 'opal::im::shareOpen',
} as const;

export type OpalEventName = (typeof EVENT_NAME)[keyof typeof EVENT_NAME];

// IM view-state the host exchanges with the chat component (outbound snapshot +
// inbound `initialMessaging`). Mirrors chat-frontend across the MF boundary.
export type MessagingViewState = {
  mode: 'ai-chat' | 'messages';
  view: 'conversation' | 'mentions' | 'threads';
  conversationId?: string;
  conversationTab?: 'artifacts' | 'messages' | 'pins';
  threadPanelId?: string;
  targetMessageId?: string;
};

// Payload shapes copied from chat-frontend's client/types/opal.ts (the host can't
// import across the Module Federation boundary), narrowed to what the host sends
// or receives. The DOM event maps are augmented from this type in
// src/types/opal-events.d.ts, so addEventListener handlers are typed by name.
export type OpalEventMap = {
  'opal::chatbox::closed': CustomEvent<undefined>;
  'opal::thread::changed': CustomEvent<{ threadId?: string | null; title?: string | null }>;
  'opal::thread::created': CustomEvent<{ threadId?: string | null; title?: string | null }>;
  'opal::thread::deleted': CustomEvent<{ threadId?: string | null; title?: string | null }>;
  'opal::thread::titleChanged': CustomEvent<{ title?: string | null }>;
  'opal::chat::setThread': CustomEvent<{ threadId?: string }>;
  'opal::sidebar::visibilityChanged': CustomEvent<{ isVisible: boolean }>;
  'opal::threads::unreadChanged': CustomEvent<{ hasUnreadThreads: boolean }>;
  'opal::im::conversationChanged': CustomEvent<{ conversationId: string }>;
  'opal::im::modeChanged': CustomEvent<{ mode: 'ai-chat' | 'messages' }>;
  'opal::im::stateChanged': CustomEvent<MessagingViewState>;
};

export type OpalEvent<K extends keyof OpalEventMap> = OpalEventMap[K];
